import React, { useState } from "react";
import { Box, InputAdornment } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { mapAdmissionForSearch, filterAdmissionsByQuery, formatDoctorName } from "../billing-api";

function formatAdmittedAt(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return String(iso);
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function PatientSearchBar({
  admissions,
  loadingAdmissions,
  searchQuery,
  onSearchChange,
  onSelect,
  selectedPatient,
  onClear,
  onRefresh,
  searchError,
  selectHint = "select one to continue",
}) {
  const [focused, setFocused] = useState(false);
  const mapped = admissions.map(mapAdmissionForSearch);
  const filtered = filterAdmissionsByQuery(mapped, searchQuery);
  const showResults = focused && !selectedPatient && (searchQuery.trim() || mapped.length > 0);

  if (selectedPatient) {
    const identityLine = [
      `UHID: ${selectedPatient.uhid || selectedPatient.patient_id}`,
      `Bed ${selectedPatient.bed || "—"}`,
      `Admission #${selectedPatient.admission_id}`,
      formatDoctorName(selectedPatient.doctor),
    ]
      .filter(Boolean)
      .join(" · ");
    // Bed type and payer come from the cached admission context, so they are
    // absent on an id-only restore — render the line only when we have them.
    const coverageLine = [
      selectedPatient.bed_type ? `Bed type: ${selectedPatient.bed_type}` : "",
      selectedPatient.payer ? `Payer: ${selectedPatient.payer}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <Box sx={{ ...ipdCardSx, p: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "6px" }}>Selected patient</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "4px" }}>{selectedPatient.name}</div>
          <div style={{ fontSize: "12px", color: C.textSub }}>{identityLine}</div>
          {coverageLine && (
            <div style={{ fontSize: "12px", color: C.textSub, marginTop: "3px" }}>{coverageLine}</div>
          )}
        </div>
        <IpdButton variant="outlined" onClick={onClear}>
          Change patient
        </IpdButton>
      </Box>
    );
  }

  return (
    <Box sx={{ ...ipdCardSx, p: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: C.text }}>Find patient</div>
        <IpdButton
          variant="outlined"
          size="small"
          onClick={onRefresh}
          disabled={loadingAdmissions}
          startIcon={<SvgIcon name="sync" size={14} color={C.textSub} />}
        >
          {loadingAdmissions ? "Loading…" : "Refresh list"}
        </IpdButton>
      </div>
      <div style={{ position: "relative" }}>
        <IpdTextField
          fullWidth
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Search by patient name, ID, bed, or admission #…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon name="search" size={16} color={C.textSub} />
              </InputAdornment>
            ),
          }}
        />
        {/* boxShadow rgba(13,35,63,0.14) has no exact IPD_COLORS.shadow equivalent — left as-is */}
        {showResults && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: IPD_COLORS.cardBg, border: `1px solid ${C.border}`, borderRadius: "10px", boxShadow: "0 12px 32px rgba(13,35,63,0.14)", maxHeight: "320px", overflowY: "auto", zIndex: 20 }}>
            {loadingAdmissions ? (
              <div style={{ padding: "20px", fontSize: "13px", color: C.textSub, textAlign: "center" }}>Loading allocated patients…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "20px", fontSize: "13px", color: C.textSub, textAlign: "center" }}>
                {searchQuery.trim() ? "No patients match your search." : "No allocated admissions found."}
              </div>
            ) : (
              filtered.map((row) => (
                <button
                  key={row.admission_id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(row)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: IPD_COLORS.cardBg, border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = IPD_COLORS.cardBg; }}
                >
                  <div style={{ fontSize: "14px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>{row.patient_name}</div>
                  <div style={{ fontSize: "12px", color: C.textSub }}>
                    UHID: {row.uhid} · Bed {row.bed_id} · Admission #{row.admission_id} · {formatDoctorName(row.doctor_name) || "—"}
                  </div>
                  <div style={{ fontSize: "11px", color: C.textSub, marginTop: "2px" }}>
                    {[
                      `Admitted ${formatAdmittedAt(row.admitted_at)}`,
                      row.status,
                      row.bed_type_name || row.bed_type_code,
                      row.payer_label,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {searchError && (
        <p style={{ margin: "10px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{searchError}</p>
      )}
      {!loadingAdmissions && !searchError && (
        <p style={{ margin: "10px 0 0", fontSize: "12px", color: C.textSub }}>
          {mapped.length} allocated admission{mapped.length === 1 ? "" : "s"} · {selectHint}
        </p>
      )}
    </Box>
  );
}
